<script lang="ts">
    import type { Snippet } from "svelte";
    import LL from "../../../i18n/i18n-svelte";
    import MegaphoneIcon from "../Icons/MegaphoneIcon.svelte";
    import type { Streamable } from "../../Space/Streamable";
    import type { VideoBoxStatus } from "../../Space/VideoBox";
    import { activePictureInPictureStore } from "../../Stores/PeerStore";
    import WebRtcVideo from "./VideoTags/WebRtcVideo.svelte";
    import LivekitVideo from "./VideoTags/LivekitVideo.svelte";
    import ScriptingVideo from "./VideoTags/ScriptingVideo.svelte";
    import { IconCameraExclamation } from "@wa-icons";

    /**
     * This component is in charge of displaying a <video> element in the center of the
     * container it is part of. It will rescale the video to fit the container both in
     * width and height, keeping the aspect ratio of the video.
     *
     * It also handles displaying a warning if no video frame is received after 3 seconds.
     *
     * @slot - The content to display on top of the video.
     */
    interface Props {
        videoEnabled: boolean;
        media: Streamable["media"];
        onvideo?: () => void;
        onnoVideo?: () => void;
        // If set to "top", the video will be at the top of the container. If set to "center", the video will be centered.
        verticalAlign: "center" | "top";
        isTalking: boolean;
        flipX: boolean;
        // If cover is true, the video will be stretched to cover the whole container (and some part of the video might be cropped).
        cover: boolean;
        // If true, the video will be displayed with a background is it does not cover the whole box
        withBackground: boolean;
        isBlocked: boolean;
        status: VideoBoxStatus;
        // If true, the video box is a megaphone space
        isMegaphoneSpace: boolean;
        // Offers zoom + pan controls over the video. Used by screen shares.
        zoomable?: boolean;
        children?: Snippet;
    }

    let {
        videoEnabled = false,
        media,
        onvideo,
        onnoVideo,
        verticalAlign = "center",
        isTalking = false,
        flipX = false,
        cover = true,
        withBackground = false,
        isBlocked = false,
        status = "connecting",
        isMegaphoneSpace = false,
        zoomable = false,
        children,
    }: Props = $props();

    // ---- zoom + pan -------------------------------------------------------
    // Conventions taken from how meeting apps actually behave:
    //  * ctrl/cmd + wheel zooms, which is ALSO what a trackpad pinch emits, so one
    //    handler covers both. Plain wheel is left alone so surrounding lists scroll.
    //  * the zoom anchors on the pointer, keeping whatever is under the cursor where
    //    it is. Zooming to the centre forces a second drag to reach what you wanted.
    //  * the buttons zoom about the centre of the current view, having no pointer.
    const ZOOM_MIN = 1;
    const ZOOM_MAX = 3;
    const ZOOM_STEP = 0.25;

    let zoom = $state(1);
    // The visible video rectangle. The wheel listener lives on the root so it can
    // never be covered, but the anchor has to be measured against THIS.
    let zoomBoxEl: HTMLDivElement | undefined = $state();
    let panX: number = $state(0);
    let panY: number = $state(0);
    let dragging = $state(false);
    let dragStartX = 0;
    let dragStartY = 0;

    let canPan = $derived(zoom > 1);
    // How far the magnified video can travel before its own edge would come into view.
    let maxPanX = $derived(Math.max(0, (videoWidth * zoom - overlayWidth) / 2));
    let maxPanY = $derived(Math.max(0, (videoHeight * zoom - overlayHeight) / 2));

    const clamp = (v: number, limit: number) => Math.min(limit, Math.max(-limit, v));

    // Clamped for display rather than on write, so a resize that shrinks the travel
    // corrects the picture without a derivation writing state.
    let panXClamped = $derived(clamp(panX, maxPanX));
    let panYClamped = $derived(clamp(panY, maxPanY));

    /**
     * Move to `next` zoom while holding the point (anchorX, anchorY) still. Anchors
     * are offsets from the centre of the box, in screen pixels; 0,0 is the centre,
     * which is what the buttons use.
     */
    function zoomTo(next: number, anchorX = 0, anchorY = 0) {
        const target = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(next * 100) / 100));
        if (target === zoom) return;
        if (target === ZOOM_MIN) {
            zoom = target;
            panX = 0;
            panY = 0;
            return;
        }
        // Bound against the travel the NEW zoom allows, so the stored offset is never
        // left out of range for the next drag to start from.
        const ratio = target / zoom;
        const nextMaxX = Math.max(0, (videoWidth * target - overlayWidth) / 2);
        const nextMaxY = Math.max(0, (videoHeight * target - overlayHeight) / 2);
        panX = clamp(anchorX - (anchorX - panX) * ratio, nextMaxX);
        panY = clamp(anchorY - (anchorY - panY) * ratio, nextMaxY);
        zoom = target;
    }

    function onWheel(event: WheelEvent) {
        // A trackpad pinch arrives here as a wheel event with ctrlKey set.
        if (!zoomable || !(event.ctrlKey || event.metaKey)) return;
        const box = zoomBoxEl?.getBoundingClientRect();
        if (!box) return;
        // Ignore the letterboxed area around the picture.
        if (
            event.clientX < box.left ||
            event.clientX > box.right ||
            event.clientY < box.top ||
            event.clientY > box.bottom
        ) {
            return;
        }
        event.preventDefault();
        const anchorX = event.clientX - (box.left + box.width / 2);
        const anchorY = event.clientY - (box.top + box.height / 2);
        // deltaY is per-notch on a mouse and finer on a trackpad; scale it so both feel sane.
        zoomTo(zoom * (1 - event.deltaY / 300), anchorX, anchorY);
    }

    function onPanStart(event: PointerEvent) {
        if (!canPan) return;
        dragging = true;
        dragStartX = event.clientX - panX;
        dragStartY = event.clientY - panY;
        (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    }

    function onPanMove(event: PointerEvent) {
        if (!dragging) return;
        event.preventDefault();
        panX = clamp(event.clientX - dragStartX, maxPanX);
        panY = clamp(event.clientY - dragStartY, maxPanY);
    }

    function onPanEnd(event: PointerEvent) {
        if (!dragging) return;
        dragging = false;
        (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
    }

    // Same rectangle the children overlay uses, so the zoom layer lines up with the
    // picture rather than the whole container.
    let boxStyle = $derived(
        videoEnabled
            ? `width: ${overlayWidth}px; height: ${overlayHeight}px; left: ${(containerWidth - overlayWidth) / 2}px;` +
              (verticalAlign === "center" ? ` top: ${(containerHeight - overlayHeight) / 2}px;` : "")
            : ""
    );

    // translate first, so the offset stays in screen pixels rather than being
    // multiplied by the zoom, which is what makes the clamping above correct.
    let videoTransform = $derived(
        [
            panXClamped || panYClamped ? `translate(${panXClamped}px, ${panYClamped}px)` : "",
            flipX ? "scaleX(-1)" : "",
            zoom !== 1 ? `scale(${zoom})` : "",
        ]
            .filter(Boolean)
            .join(" ")
    );
    let videoTransformStyle = $derived(
        videoTransform ? `-webkit-transform: ${videoTransform};transform: ${videoTransform};` : ""
    );

    function handleVideo(): void {
        displayNoVideoWarning = false;
        onvideo?.();
    }

    function handleNoVideo(): void {
        displayNoVideoWarning = true;
        onnoVideo?.();
    }

    let containerWidth: number = $state(0);
    let containerHeight: number = $state(0);
    let videoWidth: number = $state(0);
    let videoHeight: number = $state(0);
    let videoStreamWidth: number = $state(0);
    let videoStreamHeight: number = $state(0);
    let overlayWidth: number = $state(0);
    let overlayHeight: number = $state(0);
    let videoRatio: number = $state(0);

    $effect(() => {
        if (videoEnabled && containerWidth && containerHeight) {
            const containerRatio = containerWidth / containerHeight;
            // In case there is no video, we put an arbitrary ratio of 16/9 to avoid division by 0.
            const currentVideoRatio =
                videoStreamWidth && videoStreamHeight ? videoStreamWidth / videoStreamHeight : 16 / 9;
            videoRatio = currentVideoRatio;

            //debug("videoRatio:" + videoRatio + "; containerRatio: " + containerRatio + "; containerWidth: " + containerWidth + "; containerHeight: " + containerHeight +" ; videoStreamWidth: " + videoStreamWidth + "; videoStreamHeight: " + videoStreamHeight);

            if (currentVideoRatio < 1) {
                if (!cover) {
                    videoWidth = containerHeight * currentVideoRatio;
                    videoHeight = containerHeight;
                    overlayWidth = videoWidth;
                    overlayHeight = videoHeight;
                } else {
                    // In case we are on a mobile in portrait mode, we want to display a square video.
                    videoWidth = containerHeight;
                    videoHeight = containerHeight / currentVideoRatio;
                    overlayWidth = containerWidth;
                    overlayHeight = containerHeight;
                    //debug("videoRatio < 1: videoWidth: " + videoWidth + "; videoHeight: " + videoHeight);
                }
            } else if (containerRatio > currentVideoRatio) {
                if (!cover) {
                    videoWidth = containerHeight * currentVideoRatio;
                    videoHeight = containerHeight;
                    overlayWidth = videoWidth;
                    overlayHeight = videoHeight;
                } else {
                    videoWidth = containerWidth;
                    videoHeight = containerWidth / currentVideoRatio;
                    overlayWidth = containerWidth;
                    overlayHeight = containerHeight;
                }
                //debug("containerRatio > videoRatio: videoWidth: " + videoWidth + "; videoHeight: " + videoHeight);
            } else {
                if (!cover) {
                    videoWidth = containerWidth;
                    videoHeight = containerWidth / currentVideoRatio;
                    overlayWidth = videoWidth;
                    overlayHeight = videoHeight;
                } else {
                    videoWidth = containerHeight * currentVideoRatio;
                    videoHeight = containerHeight;
                    overlayWidth = containerWidth;
                    overlayHeight = containerHeight;
                }
                //debug("containerRatio <= videoRatio: videoWidth: " + videoWidth + "; videoHeight: " + videoHeight);
            }
        }
    });

    let displayNoVideoWarning = $state(false);
</script>

<div
    class="h-full w-full relative {(!cover || videoRatio < 1) && withBackground ? 'bg-contrast/80 rounded-lg' : ''}"
    onwheel={onWheel}
    bind:clientWidth={containerWidth}
    bind:clientHeight={containerHeight}
    class:flex={$activePictureInPictureStore}
    class:flex-col={$activePictureInPictureStore}
    class:justify-center={$activePictureInPictureStore}
    class:transition-all={$activePictureInPictureStore}
    class:duration-100={$activePictureInPictureStore}
    class:ease-out={$activePictureInPictureStore}
>
    {#if media?.type === "component"}
        <div class="group/centered-video absolute inset-0 flex justify-center items-center overflow-hidden">
            {#if media.component}
                {@const MediaComponent = media.component}
                <MediaComponent
                    width={containerWidth ?? 320}
                    height={containerHeight ?? (containerWidth ?? 320) * (9 / 16)}
                />
            {/if}
        </div>
    {:else}
        <div
            class="absolute overflow-hidden border-solid rounded-lg"
            class:w-full={!videoEnabled}
            class:h-full={!videoEnabled}
            class:border-transparent={!isTalking}
            class:border-secondary={isTalking}
            class:border-yellow-200={isMegaphoneSpace}
            class:border-4={isMegaphoneSpace}
            class:transition-all={$activePictureInPictureStore}
            class:duration-100={$activePictureInPictureStore}
            class:ease-out={$activePictureInPictureStore}
            style={videoEnabled
                ? "width: " +
                  overlayWidth +
                  "px; height: " +
                  overlayHeight +
                  "px; left: " +
                  (containerWidth - overlayWidth) / 2 +
                  "px;" +
                  (verticalAlign === "center" ? " top: " + (containerHeight - overlayHeight) / 2 + "px;" : "")
                : ""}
        >
            <!-- If "isMegaphoneSpace" is true, add corner markers to the video box with megaphone icon -->
            {#if isMegaphoneSpace}
                <div
                    class="absolute bottom-0 right-0 rounded-none rounded-tl-lg bg-yellow-200 backdrop-blur flex justify-center items-center gap-2 px-2 h-10 w-10 content-center z-[600]"
                >
                    <MegaphoneIcon strokeColor="stroke-black" classList="mt-1 ml-1" />
                </div>
            {/if}

            {#if !isBlocked && videoEnabled && status === "connected"}
                {#if media?.type === "webrtc"}
                    <WebRtcVideo
                        {media}
                        style={"width: " +
                            Math.ceil(videoWidth) +
                            "px; height: " +
                            Math.ceil(videoHeight) +
                            "px; " +
                            ` top: ${(containerHeight - videoHeight) / 2 - (containerHeight - overlayHeight) / 2}px;` +
                            (cover
                                ? ` left: ${(containerWidth - videoWidth) / 2 - (containerWidth - overlayWidth) / 2}px;`
                                : "") +
                            videoTransformStyle}
                        className="absolute block object-fill"
                        bind:videoWidth={videoStreamWidth}
                        bind:videoHeight={videoStreamHeight}
                        onvideo={handleVideo}
                        onnovideo={handleNoVideo}
                    />
                {:else if media?.type === "livekit"}
                    <LivekitVideo
                        {media}
                        style={"width: " +
                            Math.ceil(videoWidth) +
                            "px; height: " +
                            Math.ceil(videoHeight) +
                            "px; " +
                            ` top: ${(containerHeight - videoHeight) / 2 - (containerHeight - overlayHeight) / 2}px;` +
                            (cover
                                ? ` left: ${(containerWidth - videoWidth) / 2 - (containerWidth - overlayWidth) / 2}px;`
                                : "") +
                            videoTransformStyle}
                        className="absolute block object-fill"
                        bind:videoWidth={videoStreamWidth}
                        bind:videoHeight={videoStreamHeight}
                        onvideo={handleVideo}
                        onnovideo={handleNoVideo}
                    />
                {:else if media?.type === "scripting"}
                    <ScriptingVideo
                        {media}
                        style={"width: " +
                            Math.ceil(videoWidth) +
                            "px; height: " +
                            Math.ceil(videoHeight) +
                            "px; " +
                            ` top: ${(containerHeight - videoHeight) / 2 - (containerHeight - overlayHeight) / 2}px;` +
                            (cover
                                ? ` left: ${(containerWidth - videoWidth) / 2 - (containerWidth - overlayWidth) / 2}px;`
                                : "") +
                            videoTransformStyle}
                        className="absolute block object-fill"
                        bind:videoWidth={videoStreamWidth}
                        bind:videoHeight={videoStreamHeight}
                    />
                {:else}
                    <p>Unknown media type</p>
                {/if}
            {/if}
        </div>
        {#if displayNoVideoWarning}
            <div
                class="absolute w-full h-full aspect-video mx-auto flex justify-center items-center bg-danger text-white rounded-lg"
            >
                <div class="text-center">
                    <IconCameraExclamation font-size="20" class="text-white" />
                    <div class="text-lg text-white bold">{$LL.video.no_video_stream_received()}</div>
                    <div class="italic text-xs opacity-50">
                        {$LL.menu.sub.help()}
                    </div>
                </div>
            </div>
        {/if}

        <!-- This div represents an overlay on top of the video -->
        <div
            class={"group/centered-video absolute border-solid " +
                ((videoEnabled || !withBackground) && !isBlocked ? "" : "bg-contrast/80 backdrop-blur")}
            class:w-full={!videoEnabled || displayNoVideoWarning || isBlocked || status !== "connected"}
            class:h-full={!videoEnabled || displayNoVideoWarning || isBlocked || status !== "connected"}
            class:rounded-lg={!videoEnabled || displayNoVideoWarning || isBlocked || status !== "connected"}
            class:border-transparent={(!videoEnabled && !isTalking) || videoEnabled || isBlocked}
            class:border-secondary={(!videoEnabled && isTalking) || isBlocked}
            class:hidden={videoEnabled && !overlayHeight && !isBlocked && status !== "connected"}
            class:transition-all={$activePictureInPictureStore}
            class:duration-100={$activePictureInPictureStore}
            class:ease-out={$activePictureInPictureStore}
            style={videoEnabled && !displayNoVideoWarning
                ? "width: " +
                  overlayWidth +
                  "px; height: " +
                  overlayHeight +
                  "px; left: " +
                  (containerWidth - overlayWidth) / 2 +
                  "px;" +
                  (verticalAlign === "center" ? " top: " + (containerHeight - overlayHeight) / 2 + "px;" : "")
                : ""}
        >
            {@render children?.()}
        </div>

        {#if zoomable && videoEnabled && !displayNoVideoWarning && !isBlocked && status === "connected"}
            <div bind:this={zoomBoxEl} class="absolute z-[650] pointer-events-none" style={boxStyle}>
                {#if canPan}
                    <!-- svelte-ignore a11y_no_static_element_interactions -->
                    <div
                        class="absolute inset-0 pointer-events-auto"
                        class:cursor-grab={!dragging}
                        class:cursor-grabbing={dragging}
                        onpointerdown={onPanStart}
                        onpointermove={onPanMove}
                        onpointerup={onPanEnd}
                        onpointercancel={onPanEnd}
                    ></div>
                {/if}
                <div
                    class="absolute top-2 right-2 pointer-events-auto flex items-center gap-1 rounded bg-contrast/80 backdrop-blur p-1"
                >
                    <button
                        class="px-2 py-0.5 text-white rounded hover:bg-white/20 disabled:opacity-40"
                        disabled={zoom <= ZOOM_MIN}
                        aria-label={$LL.video.zoom_out()}
                        title={$LL.video.zoom_out()}
                        onclick={() => zoomTo(zoom - ZOOM_STEP)}
                    >
                        &minus;
                    </button>
                    <button
                        class="min-w-[3.5rem] px-1 text-center text-xs text-white rounded hover:bg-white/20"
                        aria-label={$LL.video.reset_zoom()}
                        title={$LL.video.reset_zoom()}
                        onclick={() => zoomTo(ZOOM_MIN)}
                    >
                        {Math.round(zoom * 100)}%
                    </button>
                    <button
                        class="px-2 py-0.5 text-white rounded hover:bg-white/20 disabled:opacity-40"
                        disabled={zoom >= ZOOM_MAX}
                        aria-label={$LL.video.zoom_in()}
                        title={$LL.video.zoom_in()}
                        onclick={() => zoomTo(zoom + ZOOM_STEP)}
                    >
                        +
                    </button>
                </div>
            </div>
        {/if}
    {/if}
</div>
